import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRightIcon,
  ClockIcon,
  PlusCircleIcon,
  SparklesIcon,
  ShoppingCartIcon,
  WrenchScrewdriverIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { categoriesAPI, shoppingListsAPI } from '../services/api';
import StatusToast from './StatusToast';

const PRESET_STORAGE_KEY = 'shopping-list-generator-preset-v1';
const WINDOW_DAY_OPTIONS = [30, 60, 90];
const MIN_REPEAT_OPTIONS = [1, 2, 3, 4];

const DEFAULT_PRESET = {
  hasSetup: false,
  windowDays: 60,
  minPurchaseCount: 2,
  includeMainCategoryIds: [],
};

const LIST_CARD_THEMES = [
  {
    icon: ShoppingCartIcon,
    iconWrapClassName: 'bg-blue-100 text-blue-700',
    footerToneClassName: 'text-slate-500',
  },
  {
    icon: ClockIcon,
    iconWrapClassName: 'bg-indigo-100 text-indigo-700',
    footerToneClassName: 'text-slate-500',
  },
  {
    icon: WrenchScrewdriverIcon,
    iconWrapClassName: 'bg-orange-100 text-orange-700',
    footerToneClassName: 'text-red-600',
  },
];

const SORT_OPTIONS = [
  { value: 'updated-desc', label: 'Recently updated' },
  { value: 'created-desc', label: 'Newest created' },
  { value: 'created-asc', label: 'Oldest created' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
  { value: 'items-desc', label: 'Most items' },
  { value: 'items-asc', label: 'Fewest items' },
];

const getTimeValue = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const formatDateLabel = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

const normalizePreset = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  const normalizeIds = (ids) => {
    if (!Array.isArray(ids)) {
      return [];
    }
    return Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)));
  };

  return {
    hasSetup: Boolean(source.hasSetup),
    windowDays: WINDOW_DAY_OPTIONS.includes(Number(source.windowDays)) ? Number(source.windowDays) : DEFAULT_PRESET.windowDays,
    minPurchaseCount: MIN_REPEAT_OPTIONS.includes(Number(source.minPurchaseCount))
      ? Number(source.minPurchaseCount)
      : DEFAULT_PRESET.minPurchaseCount,
    includeMainCategoryIds: normalizeIds(source.includeMainCategoryIds),
  };
};

function ShoppingLists() {
  const navigate = useNavigate();
  const [shoppingLists, setShoppingLists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeTopAction, setActiveTopAction] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createListNameDraft, setCreateListNameDraft] = useState('');
  const [createModalError, setCreateModalError] = useState('');
  const [generatorPreset, setGeneratorPreset] = useState(DEFAULT_PRESET);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetDraft, setPresetDraft] = useState(DEFAULT_PRESET);
  const [generateListNameDraft, setGenerateListNameDraft] = useState('');
  const [presetModalError, setPresetModalError] = useState('');
  const [savedListSort, setSavedListSort] = useState('updated-desc');

  const refreshLists = useCallback(async () => {
    const lists = await shoppingListsAPI.getAll();
    setShoppingLists(lists);
  }, []);

  useEffect(() => {
    try {
      const rawPreset = localStorage.getItem(PRESET_STORAGE_KEY);
      if (!rawPreset) {
        return;
      }
      const parsed = JSON.parse(rawPreset);
      setGeneratorPreset(normalizePreset(parsed));
    } catch (err) {
      console.error('Failed to restore shopping list preset:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [fetchedLists, fetchedCategories] = await Promise.all([
          shoppingListsAPI.getAll(),
          categoriesAPI.getAll(),
        ]);
        setShoppingLists(fetchedLists);
        setCategories(fetchedCategories);
      } catch (err) {
        console.error('Failed to load shopping lists:', err);
        setError(err.response?.data?.error || 'Failed to load shopping lists');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [refreshLists]);

  const topLevelCategories = useMemo(() => (
    categories
      .filter((category) => !category.parentCategoryId)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  ), [categories]);

  const sortedShoppingLists = useMemo(() => {
    const base = [...shoppingLists];
    base.sort((a, b) => {
      if (savedListSort === 'name-asc') {
        return String(a.name || '').localeCompare(String(b.name || ''));
      }
      if (savedListSort === 'name-desc') {
        return String(b.name || '').localeCompare(String(a.name || ''));
      }
      if (savedListSort === 'items-desc') {
        return Number(b.itemCount || 0) - Number(a.itemCount || 0);
      }
      if (savedListSort === 'items-asc') {
        return Number(a.itemCount || 0) - Number(b.itemCount || 0);
      }
      if (savedListSort === 'created-asc') {
        return getTimeValue(a.createdAt) - getTimeValue(b.createdAt);
      }
      if (savedListSort === 'created-desc') {
        return getTimeValue(b.createdAt) - getTimeValue(a.createdAt);
      }
      return getTimeValue(b.updatedAt) - getTimeValue(a.updatedAt);
    });
    return base;
  }, [shoppingLists, savedListSort]);

  const savePreset = (nextPreset) => {
    const normalized = normalizePreset({ ...nextPreset, hasSetup: true });
    setGeneratorPreset(normalized);
    localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(normalized));
  };

  const openPresetModal = () => {
    const availableIds = topLevelCategories.map((category) => String(category.id));
    const currentSelection = (generatorPreset.includeMainCategoryIds || []).filter((id) => availableIds.includes(id));
    setPresetDraft({
      ...generatorPreset,
      includeMainCategoryIds: currentSelection.length ? currentSelection : availableIds,
    });
    setGenerateListNameDraft('');
    setPresetModalError('');
    setShowPresetModal(true);
  };

  const closePresetModal = () => {
    setPresetModalError('');
    setShowPresetModal(false);
  };

  const openCreateModal = () => {
    setCreateListNameDraft('');
    setCreateModalError('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setCreateModalError('');
    setShowCreateModal(false);
  };

  const toggleDraftIncludeCategory = (categoryId) => {
    const id = String(categoryId || '');
    setPresetDraft((current) => {
      const includeSet = new Set(current.includeMainCategoryIds);
      if (includeSet.has(id)) {
        includeSet.delete(id);
      } else {
        includeSet.add(id);
      }
      return {
        ...current,
        includeMainCategoryIds: Array.from(includeSet),
      };
    });
  };

  const stepWindowDays = (direction) => {
    setPresetDraft((current) => {
      const index = WINDOW_DAY_OPTIONS.indexOf(Number(current.windowDays));
      const currentIndex = index >= 0 ? index : 0;
      const nextIndex = Math.min(WINDOW_DAY_OPTIONS.length - 1, Math.max(0, currentIndex + direction));
      return {
        ...current,
        windowDays: WINDOW_DAY_OPTIONS[nextIndex],
      };
    });
  };

  const stepMinRepeatCount = (direction) => {
    setPresetDraft((current) => {
      const index = MIN_REPEAT_OPTIONS.indexOf(Number(current.minPurchaseCount));
      const currentIndex = index >= 0 ? index : 0;
      const nextIndex = Math.min(MIN_REPEAT_OPTIONS.length - 1, Math.max(0, currentIndex + direction));
      return {
        ...current,
        minPurchaseCount: MIN_REPEAT_OPTIONS[nextIndex],
      };
    });
  };

  const handleCreateList = async () => {
    const name = String(createListNameDraft || '').trim();
    if (activeTopAction) {
      return;
    }

    if (!name) {
      setCreateModalError('List name is required.');
      return;
    }

    try {
      setActiveTopAction('create');
      setError('');
      setCreateModalError('');

      const created = await shoppingListsAPI.create({ name });
      setShowCreateModal(false);
      navigate(`/shopping-lists/${created.id}`);
      await refreshLists();
    } catch (err) {
      console.error('Failed to create/generate list:', err);
      setCreateModalError(err.response?.data?.error || 'Failed to create list');
    } finally {
      setActiveTopAction('');
    }
  };

  const handleGenerateFromPreset = async () => {
    const name = String(generateListNameDraft || '').trim();
    if (!name) {
      setPresetModalError('List name is required.');
      return;
    }
    if (!presetDraft.includeMainCategoryIds.length) {
      setPresetModalError('Select at least one main category to generate from.');
      return;
    }

    try {
      setActiveTopAction('generate');
      setError('');
      setPresetModalError('');

      savePreset(presetDraft);
      const response = await shoppingListsAPI.generate({
        name,
        windowDays: presetDraft.windowDays,
        minPurchaseCount: presetDraft.minPurchaseCount,
        includeMainCategoryIds: presetDraft.includeMainCategoryIds,
      });
      const created = response.shoppingList;
      setShowPresetModal(false);
      navigate(`/shopping-lists/${created.id}`);
      await refreshLists();
    } catch (err) {
      console.error('Failed to generate list:', err);
      setPresetModalError(err.response?.data?.error || 'Failed to generate list');
    } finally {
      setActiveTopAction('');
    }
  };

  const bottomErrorMessage = presetModalError || createModalError || error || '';

  if (loading) {
    return <p className="text-gray-600">Loading shopping lists...</p>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5 shadow-sm sm:rounded-3xl sm:p-5">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-xl font-semibold leading-tight text-slate-900 sm:text-2xl">Shopping Lists</h1>
          <p className="mt-1.5 max-w-2xl text-sm leading-6 text-slate-600 sm:mt-2 sm:text-base">
            Build, generate, and manage your saved lists. Shopping with Strategy shows whether a trip is likely to break your budget before you reach the register.
          </p>

          <div className="mt-3 grid grid-cols-1 gap-2.5 sm:mt-4 sm:grid-cols-2 sm:gap-3">
            <button
              type="button"
              onClick={openCreateModal}
              disabled={Boolean(activeTopAction)}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60 sm:h-11 sm:text-base"
            >
              <PlusCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              <span>Create New List</span>
            </button>

            <button
              type="button"
              onClick={openPresetModal}
              disabled={Boolean(activeTopAction)}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-200/70 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-300/70 disabled:opacity-60 sm:h-11 sm:text-base"
            >
              <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              <span>Generate with AI</span>
            </button>
          </div>

        </div>
      </section>

      {shoppingLists.length > 0 && (
        <section className="space-y-2.5">
          <div className="flex items-center justify-between gap-2 px-1">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Saved Lists</p>
            <label htmlFor="saved-list-sort" className="sr-only">Sort saved lists</label>
            <select
              id="saved-list-sort"
              value={savedListSort}
              onChange={(event) => setSavedListSort(event.target.value)}
              className="h-8 rounded-lg border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2.5 sm:space-y-3">
            {sortedShoppingLists.map((list, index) => {
              const theme = LIST_CARD_THEMES[index % LIST_CARD_THEMES.length];
              const Icon = theme.icon;
              return (
              <button
                key={list.id}
                type="button"
                onClick={() => navigate(`/shopping-lists/${list.id}`)}
                className="group w-full rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:border-blue-300 hover:bg-slate-50 sm:rounded-3xl sm:p-4"
              >
                <div className="flex items-start gap-3">
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl sm:h-11 sm:w-11 sm:rounded-2xl ${theme.iconWrapClassName}`}>
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  </div>
                </div>

                <div className="mt-2.5 sm:mt-3">
                  <p className="line-clamp-2 text-[1.08rem] font-semibold leading-tight text-slate-900 sm:text-[1.3rem]">{list.name}</p>
                  <p className="mt-1 text-xs font-medium text-slate-500 sm:text-sm">Created {formatDateLabel(list.createdAt)}</p>
                </div>

                <div className="mt-2.5 border-t border-slate-200 pt-2 sm:mt-3">
                  <div className="flex items-center justify-between text-xs font-semibold sm:text-sm">
                    <div className={`inline-flex items-center gap-1.5 ${theme.footerToneClassName}`}>
                      <UserCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                      <span>Updated {formatDateLabel(list.updatedAt || list.createdAt)}</span>
                    </div>
                    <div className="inline-flex items-center gap-1 text-slate-700">
                      <span>{Number(list.itemCount || 0)} Items</span>
                      <ChevronRightIcon className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 sm:h-4 sm:w-4" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        </section>
      )}

      {!shoppingLists.length && (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white/80 px-5 py-10 text-center">
          <p className="text-lg font-semibold text-slate-800">No saved lists yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Create your first list or generate one using your purchase history.
          </p>
        </section>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-[100] bg-black/50">
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
              <h2 className="text-lg font-semibold text-slate-900">Create New List</h2>
              <p className="mt-1 text-sm text-slate-600">Name your list to continue.</p>

              <div className="mt-4">
                <label htmlFor="create-list-name" className="mb-1 block text-sm font-medium text-slate-700">
                  List name
                </label>
                <input
                  id="create-list-name"
                  type="text"
                  value={createListNameDraft}
                  onChange={(event) => {
                    setCreateListNameDraft(event.target.value);
                    if (createModalError) {
                      setCreateModalError('');
                    }
                  }}
                  placeholder="Enter list name"
                  className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateList}
                  disabled={Boolean(activeTopAction)}
                  className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Create List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPresetModal && (
        <div className="fixed inset-0 z-[100] bg-black/50">
          <div className="absolute inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
            <div className="flex h-full w-full max-w-xl flex-col border border-gray-200 bg-white shadow-xl sm:h-auto sm:max-h-[92vh] sm:rounded-xl">
              <div className="border-b border-gray-200 px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-900">Generation Rules</h2>
                <p className="mt-1 text-sm text-gray-600">Save once and reuse for quick generation.</p>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 [webkit-overflow-scrolling:touch] overscroll-contain">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label htmlFor="generate-list-name" className="mb-1 block text-sm font-medium text-gray-700">
                      List name
                    </label>
                    <input
                      id="generate-list-name"
                      type="text"
                      value={generateListNameDraft}
                      onChange={(event) => {
                        setGenerateListNameDraft(event.target.value);
                        if (presetModalError) {
                          setPresetModalError('');
                        }
                      }}
                      placeholder="Enter list name"
                      className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
                      <p className="text-sm font-medium text-gray-800">Lookback period</p>
                      <div className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white p-0.5">
                        <button
                          type="button"
                          onClick={() => stepWindowDays(-1)}
                          disabled={presetDraft.windowDays <= WINDOW_DAY_OPTIONS[0]}
                          className="h-7 w-7 rounded-md border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          -
                        </button>
                        <span className="min-w-20 px-1 text-center text-sm font-semibold text-gray-900">
                          {presetDraft.windowDays} days
                        </span>
                        <button
                          type="button"
                          onClick={() => stepWindowDays(1)}
                          disabled={presetDraft.windowDays >= WINDOW_DAY_OPTIONS[WINDOW_DAY_OPTIONS.length - 1]}
                          className="h-7 w-7 rounded-md border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2">
                      <p className="text-sm font-medium text-gray-800">Minimum repeat count</p>
                      <div className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white p-0.5">
                        <button
                          type="button"
                          onClick={() => stepMinRepeatCount(-1)}
                          disabled={presetDraft.minPurchaseCount <= MIN_REPEAT_OPTIONS[0]}
                          className="h-7 w-7 rounded-md border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          -
                        </button>
                        <span className="min-w-20 px-1 text-center text-sm font-semibold text-gray-900">
                          {presetDraft.minPurchaseCount}+
                        </span>
                        <button
                          type="button"
                          onClick={() => stepMinRepeatCount(1)}
                          disabled={presetDraft.minPurchaseCount >= MIN_REPEAT_OPTIONS[MIN_REPEAT_OPTIONS.length - 1]}
                          className="h-7 w-7 rounded-md border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-800">Include Main Categories</p>
                      <div className="flex items-center gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => setPresetDraft((current) => ({
                            ...current,
                            includeMainCategoryIds: topLevelCategories.map((category) => String(category.id)),
                          }))}
                          className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={() => setPresetDraft((current) => ({ ...current, includeMainCategoryIds: [] }))}
                          className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <p className="mb-2 text-xs text-gray-500">Unchecked categories are excluded.</p>
                    <div className="space-y-1 pr-1">
                      {topLevelCategories.map((category) => {
                        const categoryId = String(category.id);
                        return (
                          <label key={`include-${categoryId}`} className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={presetDraft.includeMainCategoryIds.includes(categoryId)}
                              onChange={() => toggleDraftIncludeCategory(categoryId)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            {category.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                </div>
              </div>

              <div className="shrink-0 flex justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
                <button
                  type="button"
                  onClick={closePresetModal}
                  className="h-10 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerateFromPreset}
                  disabled={Boolean(activeTopAction)}
                  className="h-10 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Generate List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <StatusToast
        error={bottomErrorMessage}
        onClearError={() => {
          setError('');
          setCreateModalError('');
          setPresetModalError('');
        }}
      />
    </div>
  );
}

export default ShoppingLists;

